import { getLogger } from '@logtape/logtape';
import useSWR, { KeyedMutator } from 'swr';

import useConfig from '@/hooks/useConfig.ts';

import { Appointment } from '@/models/appointment/Appointment.ts';
import { UserAppointmentDto } from '@/models/call-system/dtos/user-appointment-dto.ts';

import { fetchAppointmentsCheckInData } from '@/services/appointmentService.ts';

import { useFilterStore } from '@/stores/filterStore.ts';

import { generateAppointmentKey, getInstanceId } from '@/utils/appointmentUtils.ts';

const logger = getLogger(['hook', 'check-in-data']);

interface CheckinDataHook {
    check_in_data: Map<string, UserAppointmentDto> | undefined;
    is_loading: boolean;
    mutate: KeyedMutator<Map<string, UserAppointmentDto>>;
    getAppointmentCheckinData: (appointment: Appointment) => UserAppointmentDto | undefined;
}

const useCheckInData = (): CheckinDataHook => {
    const { customer_id, instance_ids } = useConfig();
    const selected_date = useFilterStore((state) => state.date);

    const fetchByInstance = async (instance_id: string): Promise<UserAppointmentDto[] | undefined> => {
        try {
            const data = await fetchAppointmentsCheckInData(customer_id, instance_id, selected_date!.toString());
            return data;
        } catch (error) {
            logger.warn('Failed to fetch check-in data', { error: error });
            return undefined;
        }
    };

    const fetcher = async (): Promise<Map<string, UserAppointmentDto>> => {
        const promises = instance_ids.map((instance_id) => fetchByInstance(instance_id));
        const responses = await Promise.all(promises);
        const filtered_responses = responses.filter((response) => !!response);
        const checkin_map = new Map<string, UserAppointmentDto>();

        for (const appointment of filtered_responses.flat()) {
            checkin_map.set(generateAppointmentKey(appointment.instance_id, appointment.appointment_id), appointment);
        }

        return checkin_map;
    };

    const {
        data,
        isLoading: is_loading,
        mutate,
    } = useSWR(
        selected_date
            ? {
                  key: `checkin-data-for-${customer_id}-today`,
                  customer_id: customer_id,
                  instance_ids: instance_ids,
                  date: selected_date,
              }
            : undefined,
        fetcher,
        {
            refreshInterval: 15000,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: true,
        }
    );

    const getAppointmentCheckinData = (appointment: Appointment): UserAppointmentDto | undefined => {
        return data?.get(generateAppointmentKey(getInstanceId(appointment), appointment.id));
    };

    return {
        check_in_data: data,
        is_loading: is_loading,
        mutate: mutate,
        getAppointmentCheckinData: getAppointmentCheckinData,
    };
};

export default useCheckInData;
